import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { supabase } from '@/integrations/supabase/client';

interface UserLocationState {
  userStateProvince: string | null;
  userCountry: string | null;
  userCity: string | null;
  viewingStateProvince: string;
  viewingCountry: string;
  loading: boolean;
}

interface UseUserLocationReturn extends UserLocationState {
  setViewingLocation: (state: string, country: string) => void;
  resetToUserLocation: () => void;
  isViewingHomeLocation: boolean;
}

const STORAGE_KEY = 'pl_viewing_location';

export const useUserLocation = (): UseUserLocationReturn => {
  const { user: supabaseUser, profile: supabaseProfile } = useAuth();
  const { user: internalUser, profile: internalProfile } = useInternalAuth();
  
  const [state, setState] = useState<UserLocationState>({
    userStateProvince: null,
    userCountry: null,
    userCity: null,
    viewingStateProvince: 'California', // Default
    viewingCountry: 'United States',
    loading: true,
  });

  // Load saved viewing location from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setState(prev => ({
          ...prev,
          viewingStateProvince: parsed.state || prev.viewingStateProvince,
          viewingCountry: parsed.country || prev.viewingCountry,
        }));
      } catch (e) {
        console.error('Failed to parse saved location:', e);
      }
    }
  }, []);

  // Fetch user's profile location
  useEffect(() => {
    const fetchUserLocation = async () => {
      let stateProvince: string | null = null;
      let country: string | null = null;
      let city: string | null = null;

      // Try internal profile first
      if (internalProfile) {
        stateProvince = internalProfile.state_province || null;
        country = internalProfile.country || null;
        city = internalProfile.city || null;
      }

      // Fallback to supabase profile
      if (!stateProvince && supabaseProfile) {
        stateProvince = supabaseProfile.state_province || null;
        country = supabaseProfile.country || null;
        city = supabaseProfile.city || null;
      }

      // If we have a user location and no saved preference, use user's location
      const saved = localStorage.getItem(STORAGE_KEY);
      const useUserLoc = !saved && stateProvince;

      setState(prev => ({
        ...prev,
        userStateProvince: stateProvince,
        userCountry: country || 'United States',
        userCity: city,
        viewingStateProvince: useUserLoc ? stateProvince! : prev.viewingStateProvince,
        viewingCountry: useUserLoc ? (country || 'United States') : prev.viewingCountry,
        loading: false,
      }));
    };

    fetchUserLocation();
  }, [internalProfile, supabaseProfile]);

  const setViewingLocation = useCallback((newState: string, country: string) => {
    setState(prev => ({
      ...prev,
      viewingStateProvince: newState,
      viewingCountry: country,
    }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ state: newState, country }));
  }, []);

  const resetToUserLocation = useCallback(() => {
    if (state.userStateProvince) {
      setViewingLocation(state.userStateProvince, state.userCountry || 'United States');
    }
  }, [state.userStateProvince, state.userCountry, setViewingLocation]);

  const isViewingHomeLocation = useMemo(() => {
    return (
      state.viewingStateProvince === state.userStateProvince &&
      state.viewingCountry === state.userCountry
    );
  }, [state.viewingStateProvince, state.userStateProvince, state.viewingCountry, state.userCountry]);

  return {
    ...state,
    setViewingLocation,
    resetToUserLocation,
    isViewingHomeLocation,
  };
};
